import { WorkspaceDataplane } from "src/clients/flow-api";
import {
  AssumableRole,
  AssumableRoleScope,
  OrganizationInDB,
} from "src/clients/taktile-api";
import { pluralize } from "src/utils/stringUtils";

const EVERY = "*";

export type ScopedWorkspace = {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  /** The role the caller actually holds that reaches this workspace. */
  via: string;
  /**
   * Raw flow-folder arguments of the grants reaching this workspace: "*" for
   * every folder, a folder ID for one folder. Empty for roles without the
   * folder dimension.
   */
  folderGrants: string[];
};

export const EVERY_FOLDER = EVERY;

/** Whether a grant set lets the caller narrow to `folderId`. */
export const grantsReachFolder = (
  folderGrants: string[],
  folderId: string,
): boolean =>
  folderGrants.includes(EVERY_FOLDER) || folderGrants.includes(folderId);

export type RoleReach = {
  /** The role has no organization or workspace dimension at all. */
  platformWide: boolean;
  /** Covers every workspace in every organization; nothing is enumerated. */
  allWorkspaces: boolean;
  /** Covers every organization at the organization level, without workspaces. */
  allOrganizations: boolean;
  /** Organizations the role covers as a whole, having no workspace argument. */
  organizationNames: string[];
  /**
   * Organizations where the role covers every workspace but none are known to
   * the caller, so there is nothing to enumerate.
   */
  everyWorkspaceIn: string[];
  workspaces: ScopedWorkspace[];
};

const scopeWorkspaceId = (scope: AssumableRoleScope): string | undefined =>
  scope.workspace_id ?? undefined;

const scopeOrganizationId = (scope: AssumableRoleScope): string | undefined =>
  scope.organization_id ?? undefined;

/**
 * Turn the (organization, workspace) scopes the backend reports for a role
 * into the concrete workspaces the caller would get by assuming it.
 */
export const resolveRoleReach = (
  role: AssumableRole,
  workspaces: WorkspaceDataplane[],
  organizations: OrganizationInDB[],
): RoleReach => {
  if (role.arg_names.length === 0) {
    return {
      platformWide: true,
      allWorkspaces: false,
      allOrganizations: false,
      organizationNames: [],
      everyWorkspaceIn: [],
      workspaces: [],
    };
  }

  const organizationName = (id: string | undefined): string =>
    organizations.find((org) => org.id === id)?.name ?? "Unknown organization";

  // A backend predating the folder dimension omits flow_folder_id from the
  // scopes of folder-dimensioned roles; those grants were implicitly
  // every-folder, so treat the missing value as the wildcard.
  const hasFolderDimension = role.arg_names.includes("flow_folder_id");

  const scopeWorkspaces = (scope: AssumableRoleScope): ScopedWorkspace[] => {
    const scopeFolderGrants = scope.flow_folder_id
      ? [scope.flow_folder_id]
      : hasFolderDimension
        ? [EVERY_FOLDER]
        : [];
    const rowFor = (workspace: WorkspaceDataplane): ScopedWorkspace => ({
      id: workspace.id,
      name: workspace.name,
      organizationId: workspace.organization_id,
      organizationName: organizationName(workspace.organization_id),
      via: scope.via,
      folderGrants: scopeFolderGrants,
    });
    if (scopeOrganizationId(scope) === EVERY) {
      return workspaces.map(rowFor);
    }
    if (scope.workspace_id === EVERY) {
      return workspaces
        .filter(
          (workspace) =>
            workspace.organization_id === scopeOrganizationId(scope),
        )
        .map(rowFor);
    }
    const match = workspaces.find(
      (workspace) => workspace.id === scope.workspace_id,
    );
    return [
      {
        id: scope.workspace_id ?? "",
        name: match?.name ?? "N/A (probably deleted)",
        organizationId: scopeOrganizationId(scope) ?? "",
        organizationName: organizationName(scopeOrganizationId(scope)),
        via: scope.via,
        folderGrants: scopeFolderGrants,
      },
    ];
  };

  const wildcardScopes = role.scopes.filter(
    (scope) => scopeOrganizationId(scope) === EVERY,
  );
  const coversAllWorkspaces = wildcardScopes.some(
    (scope) => scopeWorkspaceId(scope) !== undefined,
  );
  const allOrganizations = wildcardScopes.some(
    (scope) => scopeWorkspaceId(scope) === undefined,
  );

  // Folder-dimensioned roles enumerate the workspaces behind a global wildcard
  // so each one can offer its folder picker; other roles keep the summary line.
  const expandWildcards =
    hasFolderDimension && coversAllWorkspaces && workspaces.length > 0;
  const allWorkspaces = coversAllWorkspaces && !expandWildcards;

  const workspaceScopes = allWorkspaces
    ? []
    : role.scopes.filter(
        (scope) =>
          scopeWorkspaceId(scope) !== undefined &&
          (scopeOrganizationId(scope) !== EVERY || expandWildcards),
      );

  const expandedScopes = workspaceScopes.map((scope) => ({
    scope,
    rows: scopeWorkspaces(scope),
  }));

  const everyWorkspaceIn = Array.from(
    new Set(
      expandedScopes
        .filter(
          ({ scope, rows }) =>
            scope.workspace_id === EVERY && rows.length === 0,
        )
        .map(({ scope }) => organizationName(scopeOrganizationId(scope))),
    ),
  ).sort();

  // One workspace can be reached by several grants (one per folder); merge
  // them into a single row carrying every folder grant.
  const deduplicated = expandedScopes
    .flatMap(({ rows }) => rows)
    .reduce((merged, workspace) => {
      const existing = merged.get(workspace.id);
      return merged.set(
        workspace.id,
        existing
          ? {
              ...existing,
              folderGrants: Array.from(
                new Set([...existing.folderGrants, ...workspace.folderGrants]),
              ),
            }
          : workspace,
      );
    }, new Map<string, ScopedWorkspace>());

  return {
    platformWide: false,
    allWorkspaces,
    allOrganizations,
    organizationNames: allOrganizations
      ? []
      : Array.from(
          new Set(
            role.scopes
              .filter(
                (scope) =>
                  scopeWorkspaceId(scope) === undefined &&
                  scopeOrganizationId(scope) !== undefined &&
                  scopeOrganizationId(scope) !== EVERY,
              )
              .map((scope) => organizationName(scopeOrganizationId(scope))),
          ),
        ).sort(),
    everyWorkspaceIn,
    workspaces: Array.from(deduplicated.values()).sort(
      (a, b) =>
        a.organizationName.localeCompare(b.organizationName) ||
        a.name.localeCompare(b.name),
    ),
  };
};

const describeOrganizationReach = (
  allOrganizations: boolean,
  organizationNames: string[],
): string | null => {
  if (allOrganizations) {
    return "Every organization";
  }
  if (organizationNames.length > 0) {
    return pluralize(organizationNames.length, "organization");
  }
  return null;
};

/** One-line summary of where a role would apply, for the collapsed row. */
export const describeRoleReach = (reach: RoleReach): string => {
  const {
    platformWide,
    allWorkspaces,
    allOrganizations,
    organizationNames,
    everyWorkspaceIn,
    workspaces,
  } = reach;

  if (platformWide) {
    return "Platform-wide";
  }
  if (allWorkspaces) {
    return "Every workspace in every organization";
  }

  const parts = [
    describeOrganizationReach(allOrganizations, organizationNames),
    everyWorkspaceIn.length > 0
      ? `every workspace in ${everyWorkspaceIn.join(", ")}`
      : null,
    workspaces.length > 0 ? pluralize(workspaces.length, "workspace") : null,
  ].filter((part) => part !== null);

  return parts.length > 0 ? parts.join(" · ") : "No direct access";
};
